import { useState } from "react";
import { Link } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 5;

export default function NewsList() {
  const [page, setPage] = useState(1);

  usePageMeta({
    title: "秋霞影院 - 影视资讯",
    description:
      "秋霞影院影视资讯栏目，汇集影片动态、剧集资讯、幕后制作与市场票房等最新影视相关内容，助你及时掌握影视行业动态。",
    keywords: "秋霞影院,影视资讯,影片动态,剧集资讯,影视新闻",
  });

  const totalPages = Math.max(1, Math.ceil(newsList.length / PAGE_SIZE));
  const list = newsList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="cursor-pointer transition-colors hover:text-accent-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">影视资讯</span>
      </nav>

      <div className="mt-4 border-b border-background-200 pb-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">影视资讯</h1>
        <p className="mt-1.5 max-w-2xl text-sm text-foreground-500">
          关注影片动态、剧集资讯与幕后制作，第一时间了解影视行业的最新风向
        </p>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="space-y-4">
          {list.map((item) => (
            <article
              key={item.id}
              className="group overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
            >
              <div className="flex flex-col gap-4 p-4 sm:flex-row">
                <Link to={`/news/${item.id}`} className="shrink-0 cursor-pointer sm:w-56">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - 秋霞影院资讯`}
                    className="h-40 w-full rounded-lg object-cover object-top transition-transform duration-500 group-hover:scale-[1.02] sm:h-32"
                  />
                </Link>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded bg-accent-100 px-2 py-0.5 font-medium text-accent-700">
                      {item.category}
                    </span>
                    <span className="text-foreground-400">{item.date}</span>
                  </div>
                  <h2 className="mt-2">
                    <Link
                      to={`/news/${item.id}`}
                      className="clamp-2 cursor-pointer font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg"
                    >
                      {item.title}
                    </Link>
                  </h2>
                  <p className="clamp-2 mt-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-foreground-400">{item.author}</span>
                    <Link
                      to={`/news/${item.id}`}
                      className="flex cursor-pointer items-center gap-1 text-xs font-medium text-primary-600 transition-colors hover:text-accent-600"
                    >
                      阅读全文 <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <aside className="space-y-6">
          <section className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-fire-fill text-accent-600" /> 热门资讯
            </h3>
            <ul className="mt-4 space-y-3">
              {newsList.slice(0, 5).map((item, i) => (
                <li key={item.id}>
                  <Link
                    to={`/news/${item.id}`}
                    className="flex cursor-pointer items-start gap-3 text-sm transition-colors hover:text-primary-600"
                  >
                    <span
                      className={`w-4 shrink-0 font-heading text-sm font-bold ${
                        i < 3 ? "text-accent-600" : "text-foreground-400"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="clamp-2 leading-snug text-foreground-700">{item.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h3 className="font-heading text-base font-bold text-foreground-950">资讯分类</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {["影片动态", "剧集资讯", "幕后制作", "市场票房", "行业观察", "编辑推荐"].map((c) => (
                <span
                  key={c}
                  className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                >
                  {c}
                </span>
              ))}
            </div>
          </section>
        </aside>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}